import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Package, MessageCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { generateWhatsAppLink } from '@/utils/whatsappTemplates';

const QuoteDelivery = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const suburb = searchParams.get('suburb') || '';
  const campaign = searchParams.get('campaign') || '';
  
  const [formData, setFormData] = useState({
    pickup: suburb,
    dropoff: '',
    item: '',
    datetime: '',
    name: '',
    mobile: '',
    email: '',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Store form data in sessionStorage for confirmation page
    sessionStorage.setItem('quoteData', JSON.stringify({
      ...formData,
      serviceType: 'delivery',
      campaign
    }));
    navigate('/quote/confirm');
  };

  const whatsappLink = generateWhatsAppLink('delivery', {
    pickup: formData.pickup,
    dropoff: formData.dropoff,
    item: formData.item,
    datetime: formData.datetime
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Progress Indicator */}
      <div className="bg-white border-b py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center space-x-4 max-w-2xl mx-auto">
            <div className="flex items-center">
              <div className="bg-[#039fff] text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">1</div>
              <span className="ml-2 text-sm font-medium text-[#039fff]">Details</span>
            </div>
            <div className="h-px w-12 bg-gray-300"></div>
            <div className="flex items-center">
              <div className="bg-gray-300 text-gray-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">2</div>
              <span className="ml-2 text-sm text-gray-600">Confirm</span>
            </div>
            <div className="h-px w-12 bg-gray-300"></div>
            <div className="flex items-center">
              <div className="bg-gray-300 text-gray-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">3</div>
              <span className="ml-2 text-sm text-gray-600">Done</span>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="mb-8">
              <Package className="h-12 w-12 text-[#039fff] mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-[#001A47] text-center mb-2">Delivery Quote</h1>
              <p className="text-center text-gray-600">Tell us what you need delivered</p>
            </div>

            <Card>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="pickup">Pickup Address/Suburb *</Label>
                      <Input
                        id="pickup"
                        value={formData.pickup}
                        onChange={(e) => setFormData({ ...formData, pickup: e.target.value })}
                        placeholder="e.g., Stellenbosch CBD"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="dropoff">Drop-off Address/Suburb *</Label>
                      <Input
                        id="dropoff"
                        value={formData.dropoff}
                        onChange={(e) => setFormData({ ...formData, dropoff: e.target.value })}
                        placeholder="e.g., Somerset West"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="item">Item Description *</Label>
                    <Input
                      id="item"
                      value={formData.item}
                      onChange={(e) => setFormData({ ...formData, item: e.target.value })}
                      placeholder="e.g., 3 boxes, small table"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Brief description of what you're delivering</p>
                  </div>

                  <div>
                    <Label htmlFor="datetime">Preferred Date/Time *</Label>
                    <Input
                      id="datetime"
                      type="datetime-local"
                      value={formData.datetime}
                      onChange={(e) => setFormData({ ...formData, datetime: e.target.value })}
                      required
                    />
                  </div>

                  {/* Contact Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Your Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Full name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="mobile">Mobile Number *</Label>
                      <Input
                        id="mobile"
                        type="tel"
                        value={formData.mobile}
                        onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                        placeholder="+27"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="your@email.com"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="notes">Access Notes</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Stairs, parking, access codes, etc."
                      rows={3}
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button type="submit" className="flex-1 bg-[#fb6402] hover:bg-[#e55a02]">
                      Continue
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button type="button" variant="outline" className="w-full">
                        <MessageCircle className="mr-2 h-4 w-4" />
                        WhatsApp Instead
                      </Button>
                    </a>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuoteDelivery;
